import type { QueryClient } from '@tanstack/react-query';
import { InteractionTarget, InteractionType } from '@models/api/interaction';
import type { InteractionRead } from '@models/api';
import type { UserSummary } from '@models/api';
import { type QuestionCacheItem } from './types';
import { questionInteractionsKey, interactionsKey, questionEntityKey } from './queryKeys';
import { isRecord, asArray, isUserSummary } from './helpers';
import { getQuestionEntity } from './entityStore';

// ── Interfaces (mirrored from hook so this module can be tested standalone) ──

export interface InteractionFlagState {
  meToo: boolean;
  iCanHelp: boolean;
  bookmark: boolean;
}

export interface InteractionSnapshot {
  entity: QuestionCacheItem | undefined;
  interactionsQueries: [readonly unknown[], unknown][];
  questionInteractions: unknown;
  flags: InteractionFlagState;
}

// ── Counter adjustment ──────────────────────────────────────────────────────

export const adjustQuestionCounters = (
  target: QuestionCacheItem,
  interactionType: InteractionType,
  delta: number,
) => {
  if (delta === 0) return;

  switch (interactionType) {
    case InteractionType.uplift: {
      const current = target.uplifts_count;
      const next = Math.max(0, current + delta);
      target.uplifts_count = next;
      break;
    }
    case InteractionType.me_too: {
      const current = target.me_too_count;
      const next = Math.max(0, current + delta);
      target.me_too_count = next;
      break;
    }
    case InteractionType.i_can_help: {
      const current = target.i_can_help_count;
      const next = Math.max(0, current + delta);
      target.i_can_help_count = next;
      break;
    }
    default:
      break;
  }
};

// ── Flag setter ─────────────────────────────────────────────────────────────

export const setQuestionFlag = (
  target: QuestionCacheItem,
  interactionType: InteractionType,
  value: boolean,
) => {
  switch (interactionType) {
    case InteractionType.me_too:
      target.user_has_me_too = value;
      break;
    case InteractionType.i_can_help:
      target.user_has_i_can_help = value;
      break;
    case InteractionType.bookmark:
      target.user_has_bookmarked = value;
      break;
    default:
      break;
  }
};

// ── Snapshot ────────────────────────────────────────────────────────────────

export const captureSnapshot = (
  queryClient: QueryClient,
  questionId: string,
  flags: InteractionFlagState,
): InteractionSnapshot => ({
  entity: getQuestionEntity(queryClient, questionId),
  interactionsQueries: queryClient.getQueriesData({ queryKey: interactionsKey() }),
  questionInteractions: queryClient.getQueryData(questionInteractionsKey(questionId)),
  flags: { ...flags },
});

// ── Restore ─────────────────────────────────────────────────────────────────

// Pending-toggle lifecycle is owned by the caller (via clearPendingToggle).
// `restoreSnapshot` deliberately does NOT touch `pendingTogglesRef` — clearing
// every entry here would wipe in-flight toggles for unrelated interaction
// types whose mutations are still legitimately running.
export const restoreSnapshot = (
  queryClient: QueryClient,
  questionId: string,
  snapshot: InteractionSnapshot | undefined,
  triggerUpdate: () => void,
) => {
  if (!snapshot) return;

  if (snapshot.entity !== undefined) {
    queryClient.setQueryData(questionEntityKey(questionId), snapshot.entity);
  }

  snapshot.interactionsQueries.forEach(([key, data]) => {
    queryClient.setQueryData(key, data);
  });

  if (snapshot.questionInteractions !== undefined) {
    queryClient.setQueryData(questionInteractionsKey(questionId), snapshot.questionInteractions);
  }

  triggerUpdate();
};

// ── Interaction list helpers ────────────────────────────────────────────────

export const addUserToInteractionList = (
  queryClient: QueryClient,
  questionId: string,
  interactionType: InteractionType,
  user:
    | { id: string; full_name: string | null; profile_picture: string | null; slug: string | null }
    | null
    | undefined,
  content?: string,
) => {
  if (
    !user ||
    (interactionType !== InteractionType.me_too && interactionType !== InteractionType.i_can_help)
  ) {
    return;
  }

  const summary: UserSummary = {
    id: user.id,
    full_name: user.full_name,
    profile_picture: user.profile_picture ?? null,
    slug: user.slug,
    is_anonymous: false,
    answer: interactionType === InteractionType.i_can_help ? (content ?? null) : null,
  };

  queryClient.setQueryData(questionInteractionsKey(questionId), (old: unknown) => {
    if (!isRecord(old) || !('data' in old)) return old;
    const data = (old as { data: unknown }).data;
    if (!isRecord(data)) return old;
    const key = interactionType === InteractionType.me_too ? 'me_too' : 'i_can_help';
    const current = asArray<UserSummary>(data[key], isUserSummary);
    if (current.some(entry => entry.id === summary.id)) return old;
    return {
      ...old,
      data: { ...data, [key]: [...current, summary] },
    };
  });
};

export const removeUserFromInteractionList = (
  queryClient: QueryClient,
  questionId: string,
  interactionType: InteractionType,
  userId?: string,
) => {
  if (
    !userId ||
    (interactionType !== InteractionType.me_too && interactionType !== InteractionType.i_can_help)
  ) {
    return;
  }

  queryClient.setQueryData(questionInteractionsKey(questionId), (old: unknown) => {
    if (!isRecord(old) || !('data' in old)) return old;
    const data = (old as { data: unknown }).data;
    if (!isRecord(data)) return old;
    const key = interactionType === InteractionType.me_too ? 'me_too' : 'i_can_help';
    const current = asArray<UserSummary>(data[key], isUserSummary);
    const filtered = current.filter(entry => entry.id !== userId);
    if (filtered.length === current.length) return old;
    return {
      ...old,
      data: { ...data, [key]: filtered },
    };
  });
};

export const updateInteractionsQuery = (
  queryClient: QueryClient,
  questionId: string,
  interactionType: InteractionType,
  userId: string | undefined,
  toggledOn: boolean,
  interaction?: InteractionRead | null,
  content?: string | null,
) => {
  if (!userId) return;

  queryClient.setQueriesData({ queryKey: interactionsKey() }, (old: unknown) => {
    if (!isRecord(old) || !('data' in old)) return old;
    const data = (old as { data: unknown }).data;

    const root = Array.isArray(data)
      ? {
          entries: data as InteractionRead[],
          nested: false,
          nestedData: undefined as Record<string, unknown> | undefined,
        }
      : isRecord(data) && Array.isArray(data.data)
        ? { entries: data.data as InteractionRead[], nested: true, nestedData: data }
        : null;

    if (!root) return old;

    const filtered = root.entries.filter(
      (entry: InteractionRead) =>
        !(
          entry.target_id === questionId &&
          entry.interaction_type === interactionType &&
          entry.user_id === userId
        ),
    );

    if (!toggledOn) {
      return root.nested
        ? { ...old, data: { ...(root.nestedData ?? {}), data: filtered } }
        : { ...old, data: filtered };
    }

    const payload: InteractionRead =
      interaction ??
      ({
        id: `temp-${questionId}-${interactionType}-${userId}`,
        target_id: questionId,
        target_type: InteractionTarget.question,
        interaction_type: interactionType,
        user_id: userId,
        created_at: new Date().toISOString(),
        content: content ?? undefined,
      } as InteractionRead);

    return root.nested
      ? { ...old, data: { ...(root.nestedData ?? {}), data: [...filtered, payload] } }
      : { ...old, data: [...filtered, payload] };
  });
};
